import { useEffect } from "react";

import { runInAction } from "mobx";
import { observer } from "mobx-react-lite";

import { ChatKind } from "@/api";
import { AwarenessCursors } from "@/components/awareness/AwarenessCursors";
import { AwarenessPointers } from "@/components/awareness/AwarenessPointers";
import { devTools } from "@/components/debug-panel/DevToolsStore";
import { Tiptap } from "@/components/tiptap/Tiptap";
import { useStore } from "@/store";
import { useChats } from "@/store/useChats";
import { HOTKEY_MAP } from "@/utils/hotkeys";
import { keyMatcher } from "@/utils/keyMatcher";
import { trackEvent } from "@/utils/trackEvent";

import type { DocumentModel } from "@/models/DocumentModel";

type Props = {
  doc: DocumentModel;
};

export const Doc = observer(function Doc({ doc }: Props) {
  const { textNode } = doc;
  const { view } = useStore();
  const chats = useChats();

  useEffect(() => {
    if (doc) {
      runInAction(() => {
        devTools.doc = doc;
      });
    }
  }, [doc]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.defaultPrevented) {
        return;
      }
      if (keyMatcher(HOTKEY_MAP["open-chat-with-team"], e) && chats.canOpenTeamChat) {
        const isActive = chats.isOpenChat && chats.activeChatKind === ChatKind.collective;
        if (isActive) {
          trackEvent("chat_with_team_closed", {
            source: "shortcut",
          });
          chats.closeChat();
        } else {
          trackEvent("chat_with_team_opened", {
            source: "shortcut",
          });
          chats.openChat(false);
        }
      }
      if (keyMatcher(HOTKEY_MAP["open-chat-with-bot"], e) && !chats.botChatDisabled) {
        const isActive = chats.isOpenChat && chats.activeChatKind === ChatKind.personal;
        if (isActive) {
          trackEvent("chat_with_bot_closed", {
            source: "shortcut",
          });
          chats.closeChat();
        } else {
          trackEvent("chat_with_bot_opened", {
            source: "shortcut",
          });
          chats.openChat(true);
        }
      }
      if (keyMatcher(HOTKEY_MAP["toggle-sidebars"], e)) {
        view.toggleTempHideSidebars();
      }
      if (keyMatcher("Mod+KeyZ", e)) {
        if (!view.isAnyEditorFocused) {
          doc.focus();
          doc.editor.undo();
        }
      }
      if (keyMatcher("Mod+Shift+KeyZ", e)) {
        if (!view.isAnyEditorFocused) {
          doc.focus();
          doc.editor.redo();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [doc, view, chats]);

  return (
    <>
      {/*
        Drag & drop is temporary disabled.
        The work will be tracked here: D-359
        <DocDndContext doc={doc}>
      */}
      <div className="flex flex-1 flex-col text-document-text bg-document-bg cursor-text font-mono">
        <Tiptap className="h-full" node={textNode} />
      </div>
      {/* </DocDndContext> */}
      <AwarenessCursors doc={doc} />
      <AwarenessPointers doc={doc} />
    </>
  );
});
